import * as React from 'react';

import { observer } from 'mobx-react';

import { Field, FormInput } from '@estee/elc-form';

import * as classNames from 'classnames';

import { INPUT_FIELD_HIGHLIGHTED_CLASS } from '~constants/Constants';
import { FormContext } from '~context/FormContext';
import { RampFormStore } from '~domain/entities/RampFormStore';
import { classes, textInputDataListClasses, textInputFieldClasses } from '~utils/Classes';
import { FieldValidations } from '~utils/FieldValidations';
import { useCommonConfig } from '~utils/hooks/config';
import { useRemoveHighlightOnFocus } from '~utils/hooks/field';

export interface IEmailInputProps {
    showDomainSuggestionList: boolean;
    inputLabel: string;
    name: string;
    inputType: string;
    disabled?: boolean;
    readonly?: boolean;
    fieldValidations: FieldValidations;
    listName?: string;
    isAutoCompleteDisabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formStore: RampFormStore<any>;
    highlighted?: boolean;
}

const emailSuggestionClassNames = classes('iam-email-suggestion');

interface IEmailSuggestionListProps {
    listName: string;
    fieldName: string;
    fieldValue: string;
}

const EmailSuggestionList = observer(
    ({ fieldName, fieldValue, listName }: IEmailSuggestionListProps) => {
        const { commonEmailDomains = [], mobileEmailDomains = [] } = useCommonConfig();
        const domains = React.useMemo(() => mobileEmailDomains.concat(commonEmailDomains), [
            commonEmailDomains,
            mobileEmailDomains
        ]);

        let emailSuggestions: string[] = [];
        const parts = fieldValue.split('@');
        if (parts.length === 2) {
            const [name, suffix] = parts;
            emailSuggestions = domains.reduce((options: string[], domain: string) => {
                if (domain.startsWith(suffix)) {
                    options.push(`${name}@${domain}`);
                }

                return options;
            }, []);
        }

        return (
            <datalist
                id={listName}
                className={classNames(
                    textInputDataListClasses(fieldName),
                    emailSuggestionClassNames
                )}
            >
                {emailSuggestions.map((item) => {
                    return <option value={item} key={item} />;
                })}
            </datalist>
        );
    }
);

export const EmailInput = observer(
    ({
        inputLabel,
        name,
        inputType,
        disabled = false,
        readonly = false,
        highlighted = false,
        fieldValidations,
        listName,
        showDomainSuggestionList = true,
        formStore,
        ...restProps
    }: IEmailInputProps) => {
        const { isRequired, maxLength, validations } = fieldValidations;
        const fieldValue = formStore.values[name];
        const { formName } = React.useContext(FormContext);
        const computedListName =
            listName || (formName ? `${formName}EmailSuggestion` : 'emailSuggestions');

        const onFocus = useRemoveHighlightOnFocus(name);

        return (
            <>
                <Field
                    {...restProps}
                    name={name}
                    inputType={inputType}
                    inputLabel={inputLabel}
                    customClassName={classNames(textInputFieldClasses(name), {
                        [`${INPUT_FIELD_HIGHLIGHTED_CLASS}`]: highlighted
                    })}
                    isRequired={isRequired}
                    disabled={disabled}
                    readOnly={readonly}
                    maxLength={maxLength}
                    dataTestId={`form_${name}`}
                    component={FormInput}
                    list={computedListName}
                    validations={validations}
                    onFocus={onFocus}
                />
                {showDomainSuggestionList && fieldValue && (
                    <EmailSuggestionList
                        listName={computedListName}
                        fieldName={name}
                        fieldValue={fieldValue}
                    />
                )}
            </>
        );
    }
);
